import React, { useState, useEffect, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import { Edit, DeleteOutline } from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import DistributionRuleModal from "../../components/DistributionRuleModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(8, 8, 3),
  },

  paper: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
  },

  settingOption: {
    marginLeft: "auto",
  },
  margin: {
    margin: theme.spacing(1),
  },
}));

const Settings = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);

  const [settings, setSettings] = useState([]);
  const [blockGroups, setBlockGroups] = useState(false);

  const [orphanCount, setOrphanCount] = useState(null);
  const [checkingOrphans, setCheckingOrphans] = useState(false);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  const [confirmClean, setConfirmClean] = useState(false);
  const [historyDays, setHistoryDays] = useState("7");

  const [reminderAmount, setReminderAmount] = useState("24");
  const [reminderUnit, setReminderUnit] = useState("hours");
  const [reminderTemplate, setReminderTemplate] = useState("");

  const [distributionRules, setDistributionRules] = useState([]);
  const [loadingDistributionRules, setLoadingDistributionRules] =
    useState(false);
  const [distributionRuleModalOpen, setDistributionRuleModalOpen] =
    useState(false);
  const [selectedDistributionRule, setSelectedDistributionRule] =
    useState(null);
  const [confirmDeleteRule, setConfirmDeleteRule] = useState(null);

  const canManageDistribution =
    user?.profile === "admin" || user?.profile === "owner";

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, []);

  useEffect(() => {
    const fetchCrmSettings = async () => {
      try {
        const { data } = await api.get("/crm/settings");
        setBlockGroups(Boolean(data?.blockGroups));
      } catch (err) {
        toastError(err);
      }
    };
    fetchCrmSettings();
  }, []);

  const fetchDistributionRules = async () => {
    if (!canManageDistribution) return;
    setLoadingDistributionRules(true);
    try {
      const { data } = await api.get("/distribution-rules");
      setDistributionRules(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingDistributionRules(false);
    }
  };

  useEffect(() => {
    fetchDistributionRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageDistribution]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("settings", (data) => {
      if (data.action === "update") {
        setSettings((prevState) => {
          const aux = [...prevState];
          const settingIndex = aux.findIndex((s) => s.key === data.setting.key);
          if (settingIndex === -1) {
            return [...aux, data.setting];
          }
          aux[settingIndex] = {
            ...aux[settingIndex],
            value: data.setting.value,
          };
          return aux;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!canManageDistribution) return;

    const socket = openSocket();

    socket.on("distributionRule", (data) => {
      if (data.action === "create" || data.action === "update") {
        setDistributionRules((prevState) => {
          const index = prevState.findIndex((r) => r.id === data.rule.id);
          if (index !== -1) {
            const aux = [...prevState];
            aux[index] = data.rule;
            return aux;
          }
          return [data.rule, ...prevState];
        });
      }

      if (data.action === "delete") {
        setDistributionRules((prevState) =>
          prevState.filter((r) => r.id !== data.ruleId),
        );
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [canManageDistribution]);

  const handleChangeSetting = async (e) => {
    const selectedValue = e.target.value;
    const settingKey = e.target.name;

    try {
      await api.put(`/settings/${settingKey}`, {
        value: selectedValue,
      });
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const getSettingValue = (key) => {
    const { value } = settings.find((s) => s.key === key);
    return value;
  };

  const getOptionalSettingValue = (key) =>
    settings.find((s) => s.key === key)?.value;

  const appointmentReminderEnabled =
    getOptionalSettingValue("appointmentReminderEnabled") === "enabled";
  const appointmentNotifySeller =
    getOptionalSettingValue("appointmentNotifySeller") === "enabled";

  const saveSetting = async (key, value) => {
    try {
      await api.put(`/settings/${key}`, { value });
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggleAppointmentSwitch = (key) => (e) => {
    saveSetting(key, e.target.checked ? "enabled" : "disabled");
  };

  useEffect(() => {
    const rawMinutes = settings.find(
      (s) => s.key === "appointmentReminderMinutes",
    )?.value;
    const minutes = parseInt(rawMinutes, 10);
    if (!Number.isNaN(minutes) && minutes > 0) {
      if (minutes % 1440 === 0) {
        setReminderUnit("days");
        setReminderAmount(String(minutes / 1440));
      } else {
        setReminderUnit("hours");
        setReminderAmount(String(Math.max(1, Math.round(minutes / 60))));
      }
    }
    const template = settings.find(
      (s) => s.key === "appointmentReminderTemplate",
    )?.value;
    if (template !== undefined) setReminderTemplate(template);
  }, [settings]);

  const saveReminderMinutes = (amountValue, unitValue) => {
    const n = Math.max(parseInt(amountValue, 10) || 1, 1);
    setReminderAmount(String(n));
    const minutes = n * (unitValue === "days" ? 1440 : 60);
    saveSetting("appointmentReminderMinutes", String(minutes));
  };

  const handleReminderUnitChange = (e) => {
    const unitValue = e.target.value;
    setReminderUnit(unitValue);
    saveReminderMinutes(reminderAmount, unitValue);
  };

  const handleChangeBlockGroups = async (e) => {
    const nextValue = e.target.value === "enabled";
    try {
      await api.put("/crm/settings", { blockGroups: nextValue });
      setBlockGroups(nextValue);
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    const v = settings.find((s) => s.key === "syncHistoryDays")?.value;
    if (v) setHistoryDays(v);
  }, [settings]);

  const handleSaveHistoryDays = async () => {
    const n = Math.min(Math.max(parseInt(historyDays, 10) || 1, 1), 90);
    setHistoryDays(String(n));
    try {
      await api.put("/settings/syncHistoryDays", { value: String(n) });
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleCheckOrphans = async () => {
    setCheckingOrphans(true);
    try {
      const { data } = await api.get("/tickets/orphans/count");
      setOrphanCount({ tickets: data.tickets ?? 0, leads: data.leads ?? 0 });
    } catch (err) {
      toastError(err);
    } finally {
      setCheckingOrphans(false);
    }
  };

  const handleCleanOrphans = async () => {
    setCleaningOrphans(true);
    try {
      const { data } = await api.delete("/tickets/orphans");
      toast.success(
        `${data.tickets ?? 0} ticket(s) e ${data.leads ?? 0} lead(s) removido(s).`,
      );
      setOrphanCount({ tickets: 0, leads: 0 });
    } catch (err) {
      toastError(err);
    } finally {
      setCleaningOrphans(false);
      setConfirmClean(false);
    }
  };

  const getDistributionRuleScopeLabel = (rule) => {
    if (rule.whatsapp && rule.queue) {
      return `${rule.whatsapp.name} + ${rule.queue.name}`;
    }
    if (rule.whatsapp) {
      return rule.whatsapp.name;
    }
    if (rule.queue) {
      return rule.queue.name;
    }
    return i18n.t("settings.distributionRules.scope.all");
  };

  const getDistributionRuleStrategyLabel = (strategy) => {
    return i18n.t(`distributionRuleModal.strategies.${strategy}`);
  };

  const handleOpenDistributionRuleModal = () => {
    setSelectedDistributionRule(null);
    setDistributionRuleModalOpen(true);
  };

  const handleEditDistributionRule = (rule) => {
    setSelectedDistributionRule(rule);
    setDistributionRuleModalOpen(true);
  };

  const handleCloseDistributionRuleModal = () => {
    setDistributionRuleModalOpen(false);
    setSelectedDistributionRule(null);
  };

  const handleToggleDistributionRuleEnabled = async (rule) => {
    const nextEnabled = !rule.enabled;
    setDistributionRules((prevState) =>
      prevState.map((r) =>
        r.id === rule.id ? { ...r, enabled: nextEnabled } : r,
      ),
    );
    try {
      await api.put(`/distribution-rules/${rule.id}`, {
        enabled: nextEnabled,
      });
    } catch (err) {
      toastError(err);
      setDistributionRules((prevState) =>
        prevState.map((r) =>
          r.id === rule.id ? { ...r, enabled: rule.enabled } : r,
        ),
      );
    }
  };

  const handleDeleteDistributionRule = async () => {
    if (!confirmDeleteRule) return;
    try {
      await api.delete(`/distribution-rules/${confirmDeleteRule.id}`);
      setDistributionRules((prevState) =>
        prevState.filter((r) => r.id !== confirmDeleteRule.id),
      );
      toast.success(i18n.t("settings.distributionRules.deleteSuccess"));
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmDeleteRule(null);
    }
  };

  return (
    <div className={classes.root}>
      <Container className={classes.container} maxWidth="sm">
        <Typography variant="body2" gutterBottom>
          {i18n.t("settings.title")}
        </Typography>
        <Paper className={classes.paper}>
          <Typography variant="body1">
            {i18n.t("settings.settings.userCreation.name")}
          </Typography>
          <Select
            margin="dense"
            variant="outlined"
            native
            id="userCreation-setting"
            name="userCreation"
            value={
              settings && settings.length > 0 && getSettingValue("userCreation")
            }
            className={classes.settingOption}
            onChange={handleChangeSetting}
          >
            <option value="enabled">
              {i18n.t("settings.settings.userCreation.options.enabled")}
            </option>
            <option value="disabled">
              {i18n.t("settings.settings.userCreation.options.disabled")}
            </option>
          </Select>
        </Paper>

        <Paper className={classes.paper}>
          <TextField
            id="api-token-setting"
            readonly
            label="Token Api"
            margin="dense"
            variant="outlined"
            fullWidth
            value={
              settings && settings.length > 0 && getSettingValue("userApiToken")
            }
          />
        </Paper>

        <Paper className={classes.paper}>
          <Typography variant="body1">
            Bloquear grupos (Kanban e Tickets)
          </Typography>
          <Select
            margin="dense"
            variant="outlined"
            native
            id="blockGroups-setting"
            name="blockGroups"
            value={blockGroups ? "enabled" : "disabled"}
            className={classes.settingOption}
            onChange={handleChangeBlockGroups}
          >
            <option value="enabled">Sim</option>
            <option value="disabled">Não</option>
          </Select>
        </Paper>

        <Paper
          className={classes.paper}
          style={{ flexDirection: "column", alignItems: "stretch" }}
        >
          <Typography variant="body1" gutterBottom>
            Buscar histórico antes da sincronização
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Por padrão só sincroniza de quando conectou pra frente. Se ligado,
            importa o histórico dos últimos N dias (até 90) ao conectar.
          </Typography>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 8,
            }}
          >
            <Select
              margin="dense"
              variant="outlined"
              native
              id="syncHistory-setting"
              name="syncHistory"
              value={
                settings.find((s) => s.key === "syncHistory")?.value ||
                "disabled"
              }
              onChange={handleChangeSetting}
            >
              <option value="disabled">Não buscar</option>
              <option value="enabled">Buscar</option>
            </Select>
            {(settings.find((s) => s.key === "syncHistory")?.value ||
              "disabled") === "enabled" && (
              <TextField
                type="number"
                label="Dias (até 90)"
                margin="dense"
                variant="outlined"
                inputProps={{ min: 1, max: 90 }}
                value={historyDays}
                onChange={(e) => setHistoryDays(e.target.value)}
                onBlur={handleSaveHistoryDays}
                style={{ width: 140 }}
              />
            )}
          </div>
        </Paper>

        <Paper className={classes.paper}>
          <Typography variant="body1">
            {i18n.t("settings.settings.saleAuditOnClose.name")}
          </Typography>
          <Select
            margin="dense"
            variant="outlined"
            native
            id="saleAuditOnClose-setting"
            name="saleAuditOnClose"
            value={
              settings.find((s) => s.key === "saleAuditOnClose")?.value ||
              "disabled"
            }
            className={classes.settingOption}
            onChange={handleChangeSetting}
          >
            <option value="enabled">
              {i18n.t("settings.settings.saleAuditOnClose.options.enabled")}
            </option>
            <option value="disabled">
              {i18n.t("settings.settings.saleAuditOnClose.options.disabled")}
            </option>
          </Select>
        </Paper>

        <Paper className={classes.paper}>
          <Typography variant="body1">Carregar contatos</Typography>
          <Select
            margin="dense"
            variant="outlined"
            native
            id="importContacts-setting"
            name="importContacts"
            value={
              settings.find((s) => s.key === "importContacts")?.value ||
              "disabled"
            }
            className={classes.settingOption}
            onChange={handleChangeSetting}
          >
            <option value="enabled">Sim</option>
            <option value="disabled">Não</option>
          </Select>
        </Paper>

        <Typography variant="body2" gutterBottom style={{ marginTop: 24 }}>
          Agendamento
        </Typography>
        <Paper
          className={classes.paper}
          style={{ flexDirection: "column", alignItems: "stretch" }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body1">
              Lembrete automático ao cliente pelo WhatsApp
            </Typography>
            <Switch
              checked={appointmentReminderEnabled}
              onChange={handleToggleAppointmentSwitch(
                "appointmentReminderEnabled",
              )}
              color="primary"
              className={classes.settingOption}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 8,
            }}
          >
            <Typography variant="body1">Avisar com antecedência de</Typography>
            <TextField
              type="number"
              margin="dense"
              variant="outlined"
              inputProps={{ min: 1 }}
              value={reminderAmount}
              onChange={(e) => setReminderAmount(e.target.value)}
              onBlur={() => saveReminderMinutes(reminderAmount, reminderUnit)}
              disabled={!appointmentReminderEnabled}
              style={{ width: 90 }}
            />
            <Select
              margin="dense"
              variant="outlined"
              native
              value={reminderUnit}
              onChange={handleReminderUnitChange}
              disabled={!appointmentReminderEnabled}
            >
              <option value="hours">Horas</option>
              <option value="days">Dias</option>
            </Select>
          </div>
          <TextField
            label="Template da mensagem"
            margin="dense"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={reminderTemplate}
            onChange={(e) => setReminderTemplate(e.target.value)}
            onBlur={() =>
              saveSetting("appointmentReminderTemplate", reminderTemplate)
            }
            style={{ marginTop: 8 }}
          />
          <Typography variant="caption" color="textSecondary">
            {"Placeholders: {{nome}}, {{data}}, {{hora}}"}
          </Typography>
          <div style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
            <Typography variant="body1">
              Notificar o vendedor (in-app e WhatsApp, se o usuário tiver
              número)
            </Typography>
            <Switch
              checked={appointmentNotifySeller}
              onChange={handleToggleAppointmentSwitch(
                "appointmentNotifySeller",
              )}
              color="primary"
              className={classes.settingOption}
            />
          </div>
        </Paper>

        {user?.profile === "admin" && (
          <Paper
            className={classes.paper}
            style={{ flexDirection: "column", alignItems: "stretch" }}
          >
            <Typography variant="body1" gutterBottom>
              Limpar tickets órfãos (conexões removidas)
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Remove tickets cuja conexão/número foi excluída. Apaga o ticket e
              suas mensagens — ação irreversível.
            </Typography>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 8,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCheckOrphans}
                disabled={checkingOrphans}
              >
                {checkingOrphans ? "Verificando..." : "Verificar"}
              </Button>
              {orphanCount !== null && (
                <Typography variant="body2">
                  {orphanCount.tickets} ticket(s) e {orphanCount.leads} lead(s)
                  de conexões removidas
                </Typography>
              )}
              <Button
                variant="contained"
                color="secondary"
                style={{ marginLeft: "auto" }}
                onClick={() => setConfirmClean(true)}
                disabled={
                  !orphanCount ||
                  (!orphanCount.tickets && !orphanCount.leads) ||
                  cleaningOrphans
                }
              >
                {cleaningOrphans ? "Limpando..." : "Limpar"}
              </Button>
            </div>
          </Paper>
        )}

        {canManageDistribution && (
          <>
            <Typography variant="body2" gutterBottom style={{ marginTop: 24 }}>
              {i18n.t("settings.distributionRules.title")}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {i18n.t("settings.distributionRules.description")}
            </Typography>

            {distributionRules.map((rule) => (
              <Paper
                key={rule.id}
                className={classes.paper}
                style={{ flexDirection: "column", alignItems: "stretch" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Typography variant="body1">
                      {getDistributionRuleScopeLabel(rule)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {getDistributionRuleStrategyLabel(rule.strategy)}
                      {" · "}
                      {i18n.t("settings.distributionRules.membersCount", {
                        count: rule.members?.length || 0,
                      })}
                    </Typography>
                  </div>
                  <Switch
                    checked={rule.enabled}
                    onChange={() => handleToggleDistributionRuleEnabled(rule)}
                    color="primary"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleEditDistributionRule(rule)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setConfirmDeleteRule(rule)}
                  >
                    <DeleteOutline />
                  </IconButton>
                </div>
              </Paper>
            ))}

            {!loadingDistributionRules && distributionRules.length === 0 && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {i18n.t("settings.distributionRules.empty")}
              </Typography>
            )}

            <div style={{ display: "flex", marginTop: 8, marginBottom: 24 }}>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: "auto" }}
                onClick={handleOpenDistributionRuleModal}
              >
                {i18n.t("settings.distributionRules.buttons.add")}
              </Button>
            </div>
          </>
        )}

        <ConfirmationModal
          title="Limpar tickets/leads órfãos"
          open={confirmClean}
          onClose={() => setConfirmClean(false)}
          onConfirm={handleCleanOrphans}
        >
          {`Isso apagará ${orphanCount?.tickets || 0} ticket(s) (com suas mensagens) e ${orphanCount?.leads || 0} lead(s) de conexões removidas. Esta ação não pode ser desfeita. Deseja continuar?`}
        </ConfirmationModal>

        {canManageDistribution && (
          <>
            <DistributionRuleModal
              open={distributionRuleModalOpen}
              onClose={handleCloseDistributionRuleModal}
              rule={selectedDistributionRule}
              onSaved={fetchDistributionRules}
            />
            <ConfirmationModal
              title={i18n.t(
                "settings.distributionRules.confirmationModal.title",
              )}
              open={Boolean(confirmDeleteRule)}
              onClose={() => setConfirmDeleteRule(null)}
              onConfirm={handleDeleteDistributionRule}
            >
              {i18n.t("settings.distributionRules.confirmationModal.message")}
            </ConfirmationModal>
          </>
        )}
      </Container>
    </div>
  );
};

export default Settings;
